import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '../components/Brand/Icon';
import message from '../components/Brand/brandMessage';
import api from '../services';
import useSessionIdentity, { getSessionIdentity } from '../hooks/useSessionIdentity';

export default function useLocationPolicy(open) {
  const session = useSessionIdentity();
  const { t } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const request = useRef(0), controller = useRef(null), notice = useRef(null);
  const key = policy?.available ? `${policy.version}:${policy.country_code}:${policy.next_change_number}` : null;
  const needsAcceptance = Boolean(policy?.can_change && policy?.requires_confirmation && accepted !== key);
  const cancel = useCallback(() => {
    request.current++;
    controller.current?.abort();
    notice.current?.();
  }, []);

  const refresh = useCallback(async () => {
    const version = ++request.current;
    controller.current?.abort();
    controller.current = new AbortController();
    setLoading(true); setPolicy(null); setAccepted(null);
    try {
      const result = await api.userPreferences(undefined, { signal: controller.current.signal });
      if (version === request.current && session === getSessionIdentity()) setPolicy(result?.success ? result.location_policy || null : null);
    } catch { /* Missing policy keeps country controls closed; other settings work. */ }
    finally { if (version === request.current && session === getSessionIdentity()) setLoading(false); }
  }, [session]);
  useEffect(() => {
    if (open) refresh();
    else { setAccepted(null); setPolicy(null); }
    return cancel;
  }, [open, refresh, cancel]);

  const showWarning = useCallback(() => {
    if (!open || !needsAcceptance || session !== getSessionIdentity()) return;
    notice.current?.();
    notice.current = message.warning({ duration: 0, layout: 'stacked',
      content: t(`location:${policy.cooldown_months === 6 ? 'confirmSixMonths' : 'confirmYear'}`),
      closeLabel: t('common.cancel', { defaultValue: 'Cancel' }),
      action: { label: t('location:accept'), icon: <CheckIcon style={{ color: '#0AA555' }} />,
        onClick: () => { if (session === getSessionIdentity()) setAccepted(key); } },
    });
  }, [open, needsAcceptance, policy?.cooldown_months, key, t, session]);
  useEffect(() => {
    showWarning();
    return () => notice.current?.();
  }, [showWarning]);

  return { policy, loading, refresh, showWarning, needsAcceptance,
    allowed: Boolean(open && policy?.available && policy.can_change && !loading && !needsAcceptance),
    confirmation: policy?.requires_confirmation && accepted === key
      ? { version: policy.version, change_number: policy.next_change_number } : undefined };
}
